import { useEffect, useMemo, useState } from "react";
import { Maximize2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { gql } from "@apollo/client";
import getLoginId from "@zeliot/common/utils/getLoginId";
import client from "src/api/apollo/apolloClient";

const GET_VEHICLE_STATS = gql`
  query ($clientLoginId: Int, $zoneIds: [Int], $period: String) {
    getVehicleStatesStatistiques(clientLoginId: $clientLoginId, zoneIds: $zoneIds, period: $period) {
      nbr_vehicles
      nbr_vehicles_en_circulation
      nbr_vehicule_au_ralentie
      nbr_vehicles_en_arret
      nbr_vehicles_parquer
      nbr_vehicles_accidenter
      nbr_vehicles_au_garage
    }
  }
`;

const DEFAULT_STATS = {
  nbr_vehicles: 0,
  nbr_vehicles_en_circulation: 0,
  nbr_vehicule_au_ralentie: 0,
  nbr_vehicles_en_arret: 0,
  nbr_vehicles_parquer: 0,
  nbr_vehicles_accidenter: 0,
  nbr_vehicles_au_garage: 0,
};

const REQUEST_DEDUP_MS = 1200;
const recentVehicleStatsRequests = new Map();

export const VehiclesChart = ({ openModal, selectedAdministrativeIds }) => {
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debouncedZoneIds, setDebouncedZoneIds] = useState([]);

  const selectedAdministrativeIdsArray = useMemo(() => {
    const ids = selectedAdministrativeIds || {};
    const raw = [
      ...(Array.isArray(ids.regionsids) ? ids.regionsids : []),
      ...(Array.isArray(ids.departementids) ? ids.departementids : []),
      ...(Array.isArray(ids.arrondissementids) ? ids.arrondissementids : []),
      ...(Array.isArray(ids.groupementids) ? ids.groupementids : []),
      ...(Array.isArray(ids.quartierids) ? ids.quartierids : []),
      ...(Array.isArray(ids.zoneids) ? ids.zoneids : []),
    ];
    return [...new Set(raw.map((v) => Number(v)).filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
  }, [selectedAdministrativeIds]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedZoneIds(selectedAdministrativeIdsArray);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedAdministrativeIdsArray]);



  useEffect(() => {
    let isCancelled = false;

    const fetchVehicleStatesStatistiques = async () => {
      const requestKey = `${debouncedZoneIds.join(",") || "all"}|Today`;
      const now = Date.now();
      const lastAt = recentVehicleStatsRequests.get(requestKey);
      if (lastAt && now - lastAt < REQUEST_DEDUP_MS) {
        return;
      }
      recentVehicleStatsRequests.set(requestKey, now);

      setIsLoading(true);
      setError(null);
      try {
        const variables = {
          clientLoginId: getLoginId(),
          zoneIds: debouncedZoneIds,
          period: "Today",
        };

        const response = await client.query({
          query: GET_VEHICLE_STATS,
          variables,
          fetchPolicy: "cache-first",
        });

        const payload = response?.data?.getVehicleStatesStatistiques;
        if (!isCancelled) {
          setStats(payload ? { ...DEFAULT_STATS, ...payload } : DEFAULT_STATS);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err);
          setStats(DEFAULT_STATS);
        }
        console.error("Erreur récupération stats véhicules:", err);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchVehicleStatesStatistiques();

    return () => {
      isCancelled = true;
    };
  }, [debouncedZoneIds]);

  const data = useMemo(
    () => [
      { name: "En circulation", value: Number(stats.nbr_vehicles_en_circulation || 0), color: "#22c55e" },
      { name: "À l'arrêt", value: Number(stats.nbr_vehicles_en_arret || 0), color: "#facc15" },
      { name: "Au ralenti", value: Number(stats.nbr_vehicule_au_ralentie || 0), color: "#f97316" },
      { name: "Parqués", value: Number(stats.nbr_vehicles_parquer || 0), color: "#3b82f6" },
      { name: "Accidentés", value: Number(stats.nbr_vehicles_accidenter || 0), color: "#dc2626" },
      { name: "Au garage", value: Number(stats.nbr_vehicles_au_garage || 0), color: "#8b5cf6" },
    ],
    [stats]
  );

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">État des véhicules</h3>
        <div className="flex items-center gap-2">
          <button className="p-1.5 rounded hover:bg-secondary" onClick={openModal}>
            <Maximize2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {error ? (
        <div className="text-sm text-destructive">Impossible de charger les statistiques.</div>
      ) : (
        <div className="flex items-center gap-6">
          <div className="relative w-40 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} innerRadius={50} outerRadius={70} paddingAngle={2} dataKey="value">
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-800">
                {isLoading ? "..." : Number(stats.nbr_vehicles || 0)}
              </span>
              <span className="text-xs text-gray-500">Véhicules</span>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            {data.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-gray-600">
                  {item.name}: {isLoading ? "..." : item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
